import imageCompression from 'browser-image-compression';
import { auth } from '../config/firebase';
import { AppError, StorageError } from '../lib/errors';

/**
 * Cliente HTTP para gestionar las fotos de mascotas en Supabase Storage,
 * mediadas por el worker externo (cron + web service).
 *
 * Pivot desde Firebase Storage (Fase 7.E → migración 2026-05-19): el proyecto
 * Firebase nuevo no puede activar Storage sin plan Blaze. Las fotos viven en
 * Supabase Storage (bucket privado `pet-photos`) y el worker las protege
 * detrás de signed URLs con TTL corto (10 min).
 *
 * Flujo de upload:
 *  1. Cliente valida + comprime (igual que antes, sigue siendo client-side).
 *  2. Cliente pide al worker una URL firmada para subir → POST /api/photos/upload-url.
 *  3. Cliente sube el blob directo a Supabase via PUT a esa signed URL.
 *  4. Cliente le dice a Firestore que el pet tiene foto (pet.photoExt = 'jpg').
 *
 * Flujo de lectura: el componente que muestra la foto pide una signed URL
 * fresca al worker cada vez (con cache en memoria via hook usePetPhoto).
 *
 * Decisión: SIEMPRE subimos como JPEG. La compresión client-side
 * (`browser-image-compression`) convierte cualquier input a JPEG (configurado
 * en COMPRESSION_OPTIONS.fileType). Por eso `pet.photoExt` siempre es `'jpg'`
 * cuando hay foto, y `null` cuando no.
 */

const COMPRESSION_OPTIONS = {
  maxSizeMB: 0.5,
  maxWidthOrHeight: 1024,
  // useWebWorker: false — la lib intenta cargar su worker desde un CDN
  // externo (cdn.jsdelivr.net) al crearlo, lo que viola nuestro CSP
  // (script-src sin permitir CDN externo). Para mantener el CSP cerrado,
  // comprimimos en el main thread. Trade-off: bloquea UI ~1-3 segundos
  // para fotos grandes — aceptable para el flujo de upload (el user ya
  // espera tras tocar "Guardar"). Si en el futuro queremos comprimir
  // off-thread sin abrir CSP, bundlear el worker localmente o usar
  // OffscreenCanvas + WebWorker propio servido desde /assets.
  useWebWorker: false,
  fileType: 'image/jpeg',
  initialQuality: 0.85,
};

const ACCEPTED_INPUT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
];

const MAX_INPUT_SIZE_MB = 15;

/**
 * Extensión que el worker conoce y firma. Siempre 'jpg' porque comprimimos
 * a JPEG antes de subir.
 */
export const PHOTO_EXT = 'jpg';

const getWorkerBaseUrl = () => {
  const url = import.meta.env.VITE_WORKER_URL;
  if (!url) {
    throw new AppError(
      'No está configurada la URL del worker. Falta VITE_WORKER_URL en .env.local.',
      'WORKER_URL_MISSING'
    );
  }
  // Quitar trailing slash para no formar "//api/..." al concatenar.
  return url.replace(/\/$/, '');
};

const getIdToken = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new AppError(
      'No hay sesión activa. Iniciá sesión de nuevo.',
      'NO_AUTH'
    );
  }
  try {
    return await user.getIdToken(/* forceRefresh */ false);
  } catch (err) {
    throw new AppError(
      'No se pudo obtener el token de autenticación.',
      'TOKEN_FETCH_FAILED',
      err
    );
  }
};

const workerFetch = async (path, { method = 'GET', body, idToken } = {}) => {
  const url = `${getWorkerBaseUrl()}${path}`;
  const headers = {
    Authorization: `Bearer ${idToken}`,
  };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  let response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    // Sin conexión, CORS, worker caído, etc.
    throw new StorageError(
      'No se pudo contactar al servidor de fotos.',
      err
    );
  }

  if (response.status === 204) return null;

  let data = null;
  try {
    data = await response.json();
  } catch {
    // Sin cuerpo JSON — algunos errores HTTP no traen body.
  }

  if (!response.ok) {
    const err = new StorageError(
      data?.message || `El servidor respondió ${response.status}`,
      null
    );
    err.status = response.status;
    err.serverCode = data?.error;
    throw err;
  }

  return data;
};

/**
 * Comprime y sube la foto de una mascota.
 * Devuelve `{ ext, path }`. El caller persiste `ext` en `pet.photoExt`.
 *
 * Validaciones tempranas (idénticas al modelo anterior):
 *  - tipo de input en ACCEPTED_INPUT_TYPES
 *  - tamaño crudo ≤ MAX_INPUT_SIZE_MB
 *
 * Errores:
 *  - AppError('INVALID_INPUT' | 'INVALID_FILE_TYPE' | 'FILE_TOO_LARGE' | 'NO_AUTH' | 'WORKER_URL_MISSING'): falla del usuario / config
 *  - StorageError: falla al comprimir, contactar al worker, o subir a Supabase
 */
export const uploadPetPhoto = async ({ petId, file }) => {
  if (!petId) {
    throw new AppError('petId requerido', 'INVALID_INPUT');
  }
  if (!file) {
    throw new AppError('Archivo requerido', 'INVALID_INPUT');
  }
  if (!ACCEPTED_INPUT_TYPES.includes(file.type)) {
    throw new AppError(
      'Formato no soportado. Usá JPG, PNG, WebP o HEIC.',
      'INVALID_FILE_TYPE'
    );
  }
  if (file.size > MAX_INPUT_SIZE_MB * 1024 * 1024) {
    throw new AppError(
      `La imagen supera el máximo de ${MAX_INPUT_SIZE_MB} MB`,
      'FILE_TOO_LARGE'
    );
  }

  let compressed;
  try {
    compressed = await imageCompression(file, COMPRESSION_OPTIONS);
  } catch (err) {
    throw new StorageError('No se pudo procesar la imagen', err);
  }

  const idToken = await getIdToken();

  // 1. Pedir signed URL al worker.
  const { signedUrl, path, token } = await workerFetch('/api/photos/upload-url', {
    method: 'POST',
    body: { petId, ext: PHOTO_EXT },
    idToken,
  });

  // 2. Subir el blob directo a Supabase. La signed URL incluye permisos para
  // PUT con el token; mandamos el header `x-upsert: true` para que sobreescriba
  // si ya existía una foto con el mismo path.
  try {
    const uploadResponse = await fetch(signedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'image/jpeg',
        'x-upsert': 'true',
        // Supabase exige este header con el token de la signed URL en
        // versiones recientes de Storage. Si la versión del bucket es vieja
        // y no lo requiere, lo ignora sin error.
        Authorization: `Bearer ${token}`,
      },
      body: compressed,
    });
    if (!uploadResponse.ok) {
      throw new Error(
        `Supabase respondió ${uploadResponse.status} al subir.`
      );
    }
  } catch (err) {
    throw new StorageError('No se pudo subir la foto', err);
  }

  return { ext: PHOTO_EXT, path };
};

/**
 * Borra todas las posibles extensiones de la foto del pet. Idempotente.
 */
export const deletePetPhoto = async ({ petId }) => {
  if (!petId) {
    throw new AppError('petId requerido', 'INVALID_INPUT');
  }
  const idToken = await getIdToken();
  await workerFetch(`/api/photos/${petId}`, {
    method: 'DELETE',
    idToken,
  });
};

/**
 * Pide al worker una URL firmada para mostrar la foto del pet.
 * Devuelve `{ signedUrl, expiresInSeconds }` o `null` si la foto no existe
 * (pet sin foto, o se borró desde Supabase manualmente).
 *
 * El caller (hook usePetPhoto) maneja el TTL y refresca antes de que expire.
 */
export const getPetPhotoSignedUrl = async ({ petId, ext = PHOTO_EXT }) => {
  if (!petId) {
    throw new AppError('petId requerido', 'INVALID_INPUT');
  }
  const idToken = await getIdToken();
  try {
    return await workerFetch(
      `/api/photos/${petId}?ext=${encodeURIComponent(ext)}`,
      { idToken }
    );
  } catch (err) {
    if (err instanceof StorageError && err.status === 404) {
      return null;
    }
    throw err;
  }
};

// =====================================================================
// Health record photos (Sprint 3)
// =====================================================================
// Patrón idéntico al de pet photos pero con path triple-keyed
// (familyId/petId/recordId.ext) y bucket distinto ('health-records').

/**
 * Comprime y sube la foto de un health record. Devuelve `{ ext, path }`.
 * El caller (HealthContext.uploadHealthRecordPhoto) persiste `ext` en
 * `healthRecord.photoExt`.
 */
export const uploadHealthPhoto = async ({ petId, recordId, file }) => {
  if (!petId) throw new AppError('petId requerido', 'INVALID_INPUT');
  if (!recordId) throw new AppError('recordId requerido', 'INVALID_INPUT');
  if (!file) throw new AppError('Archivo requerido', 'INVALID_INPUT');
  if (!ACCEPTED_INPUT_TYPES.includes(file.type)) {
    throw new AppError(
      'Formato no soportado. Usá JPG, PNG, WebP o HEIC.',
      'INVALID_FILE_TYPE'
    );
  }
  if (file.size > MAX_INPUT_SIZE_MB * 1024 * 1024) {
    throw new AppError(
      `La imagen supera el máximo de ${MAX_INPUT_SIZE_MB} MB`,
      'FILE_TOO_LARGE'
    );
  }

  let compressed;
  try {
    compressed = await imageCompression(file, COMPRESSION_OPTIONS);
  } catch (err) {
    throw new StorageError('No se pudo procesar la imagen', err);
  }

  const idToken = await getIdToken();

  const { signedUrl, path, token } = await workerFetch(
    '/api/health-photos/upload-url',
    {
      method: 'POST',
      body: { petId, recordId, ext: PHOTO_EXT },
      idToken,
    }
  );

  try {
    const uploadResponse = await fetch(signedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'image/jpeg',
        'x-upsert': 'true',
        Authorization: `Bearer ${token}`,
      },
      body: compressed,
    });
    if (!uploadResponse.ok) {
      throw new Error(`Supabase respondió ${uploadResponse.status} al subir.`);
    }
  } catch (err) {
    throw new StorageError('No se pudo subir la foto', err);
  }

  return { ext: PHOTO_EXT, path };
};

export const deleteHealthPhoto = async ({ petId, recordId }) => {
  if (!petId) throw new AppError('petId requerido', 'INVALID_INPUT');
  if (!recordId) throw new AppError('recordId requerido', 'INVALID_INPUT');
  const idToken = await getIdToken();
  await workerFetch(
    `/api/health-photos/${petId}/${recordId}`,
    { method: 'DELETE', idToken }
  );
};

export const getHealthPhotoSignedUrl = async ({ petId, recordId, ext = PHOTO_EXT }) => {
  if (!petId) throw new AppError('petId requerido', 'INVALID_INPUT');
  if (!recordId) throw new AppError('recordId requerido', 'INVALID_INPUT');
  const idToken = await getIdToken();
  try {
    return await workerFetch(
      `/api/health-photos/${petId}/${recordId}?ext=${encodeURIComponent(ext)}`,
      { idToken }
    );
  } catch (err) {
    if (err instanceof StorageError && err.status === 404) {
      return null;
    }
    throw err;
  }
};
